/* search-typesense.js — Uhrinstinkt-Suche über Typesense (search.uhrinstinkt.de)
   Werner 03.09.2026: eigene Suche auf AWS, volle Kontrolle; Collections bleiben nativ.
   1. Autocomplete: patcht predictive-search.getResults(); rendert dasselbe Markup wie die
      native Section (predictive-search__*), damit Tastatur-Navigation, Overlay und CSS
      unverändert funktionieren. Bei Fehlern -> natives Verhalten.
   2. Suchseite: [data-ts-search] rendert Facetten + Karten (Markup wie product-card.liquid).
      Bei Fehlern -> natives Ergebnis ([data-ts-native]) wird wieder eingeblendet.
   Konfiguration: theme.settings.tsSearch (snippets/search-typesense-config.liquid). */
(() => {
  // Das Script wird im Header UND in der Such-Section eingebunden -> nur einmal ausführen.
  if (window.__tsSearchInit) return;
  window.__tsSearchInit = true;
  const showNative = () => { const n = document.querySelector('[data-ts-native]'); if (n) { n.hidden = false; n.classList.add('is-fallback'); } };
  const cfg = window.theme && theme.settings && theme.settings.tsSearch;
  if (!cfg || !cfg.host || !cfg.key) { showNative(); return; }
  const T = cfg.strings || {};
  const host = String(cfg.host).replace(/^https?:\/\//, '').replace(/\/+$/, '');
  const base = `https://${host}/collections/${encodeURIComponent(cfg.collection || 'products')}/documents/search`;
  const TIMEOUT_MS = Number(cfg.timeoutMs) || 4000;

  // Reihenfolge = Gewichtung (siehe aws-lambdas/uhrinstinkt-search-indexer/search-test.mjs)
  const COMMON = {
    query_by: 'title,vendor,referenz,referenz_norm,search_terms,sku,tags,kaliber,typ,funktionen,zifferblatt,band,werk,gehaeuse,zustand',
    query_by_weights: '10,6,9,9,8,8,4,3,2,2,2,2,2,2,2',
    num_typos: '2,1,0,0,0,0,0,1,1,1,1,1,1,1,1',
    infix: 'off,off,off,always,off,off,off,off,off,off,off,off,off,off,off',
    prefix: 'true,true,true,true,true,true,false,true,true,true,true,true,true,true,true',
    drop_tokens_threshold: 1,
  };
  const SORTS = {
    relevance: '_text_match:desc,rank:desc,created_ts:desc',
    price_asc: 'price:asc,rank:desc',
    price_desc: 'price:desc,rank:desc',
    newest: 'created_ts:desc,rank:desc',
  };
  const FIELDS = 'id,handle,title,vendor,referenz,price,compare_at_price,preis_auf_anfrage,in_stock,available,has_image,image,condition,zustand,created_ts';

  let preconnected = false;
  const preconnect = () => {
    if (preconnected) return;
    preconnected = true;
    const l = document.createElement('link');
    l.rel = 'preconnect'; l.href = `https://${host}`; l.crossOrigin = '';
    document.head.appendChild(l);
  };

  // Anonyme Sitzungskennung nur für die Such-Analyse: Typesense fasst damit Tipp-Zwischenstufen
  // eines Nutzers zusammen (4-Sekunden-Pause). Zufallswert je Browser-Sitzung, keine Personendaten.
  const sessionId = (() => {
    try {
      let id = sessionStorage.getItem('ts_sid');
      if (!id) { id = Math.random().toString(36).slice(2, 12) + Date.now().toString(36); sessionStorage.setItem('ts_sid', id); }
      return id;
    } catch { return 'anon'; }
  })();

  // Timeout: hängt der Endpunkt, greift nach TIMEOUT_MS der native Fallback (Fehlername
  // 'TimeoutError', damit er nicht wie ein Nutzer-Abbruch ('AbortError') verschluckt wird).
  async function search(params, signal) {
    const u = new URL(base);
    Object.entries({ ...COMMON, ...params, 'x-typesense-user-id': sessionId }).forEach(([k, v]) => { if (v !== undefined && v !== '' && v !== null) u.searchParams.set(k, v); });
    const ctrl = new AbortController();
    let timedOut = false;
    const timer = setTimeout(() => { timedOut = true; ctrl.abort(); }, TIMEOUT_MS);
    if (signal) signal.addEventListener('abort', () => ctrl.abort(), { once: true });
    try {
      const r = await fetch(u, { headers: { 'X-TYPESENSE-API-KEY': cfg.key }, signal: ctrl.signal });
      if (!r.ok) throw new Error(`typesense ${r.status}`);
      return await r.json();
    } catch (e) {
      if (timedOut) { const t = new Error(`typesense timeout ${TIMEOUT_MS} ms`); t.name = 'TimeoutError'; throw t; }
      throw e;
    } finally { clearTimeout(timer); }
  }

  const esc = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  const fmt = new Intl.NumberFormat(cfg.locale || 'de', { style: 'currency', currency: cfg.currency || 'EUR', minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const money = (v) => fmt.format(Number(v) || 0);
  const tpl = (s, vars) => String(s || '').replace(/\{\{\s*(\w+)\s*\}\}/g, (_, k) => (vars[k] ?? ''));
  const img = (url, w) => { if (!url) return ''; try { const u = new URL(url); u.searchParams.set('width', w); return u.toString(); } catch { return url; } };
  const productUrl = (d) => `${String(cfg.root || '/').replace(/\/$/, '')}/products/${d.handle}`;
  const isCpo = (d) => d.condition === 'cpo' && ['sehr gut', 'gut'].includes(String(d.zustand || '').toLowerCase());
  const isNew = (d) => d.in_stock && (Date.now() / 1000 - (d.created_ts || 0)) < 30 * 86400;
  const onSale = (d) => d.available && typeof d.price === 'number' && d.compare_at_price > d.price;

  function priceHtml(d, cls) {
    if (d.preis_auf_anfrage) return `<span class="${cls} ${cls}--on-request">${esc(T.on_request)}</span>`;
    let h = `<span class="${cls}">${esc(money(d.price))}</span>`;
    if (onSale(d)) h += ` <span class="${cls}-compare">${esc(money(d.compare_at_price))}</span>`;
    return h;
  }

  /* ---------- 1. Autocomplete ---------- */
  function autocompleteHtml(res, inputId, q) {
    const hits = res.hits || [];
    if (!hits.length) {
      return `<div class="predictive-search absolute bg-theme-bg text-theme-text text-start"><div class="predictive-search__scroller overflow-auto"><div class="predictive-search__results flex flex-col"><div class="predictive-search__no-results">${esc(tpl(T.no_results, { terms: q }))}</div></div></div></div>`;
    }
    const items = hits.map((h, i) => {
      const d = h.document;
      const media = d.image
        ? `<div class="predictive-result__media media relative"><img src="${esc(img(d.image, 112))}" alt="${esc(d.title)}" loading="eager" width="56" height="56" class="predictive-result__media"></div>`
        : `<div class="predictive-result__media media relative ts-ac__noimg" aria-hidden="true"></div>`;
      // Werner 30.10.2026: bei „Preis auf Anfrage“ kein „Verkauft“ (normale Abverkaufsuhren ohne Bestand behalten es)
      const avail = !d.available ? (d.preis_auf_anfrage ? '' : `<span class="ts-ac__status ts-ac__status--sold">${esc(T.sold)}</span>`)
        : d.in_stock ? `<span class="ts-ac__status ts-ac__status--in-stock">${esc(T.in_stock)}</span>` : '';
      return `<li class="predictive-search__item" role="option" id="${esc(inputId)}-opt-${i}"><a class="predictive-result flex items-start focus-inset js-search-link" href="${esc(productUrl(d))}">${media}<div class="predictive-result__info flex-auto"><div class="ts-ac__vendor">${esc(d.vendor)}</div><h3 class="predictive-result__title h6">${esc(d.title)}</h3>${d.referenz ? `<div class="predictive-result__sub-title text-sm">${esc(T.ref)} ${esc(d.referenz)}</div>` : ''}<div class="predictive-result__sub-title text-sm ts-ac__price">${priceHtml(d, 'ts-ac__amount')}${avail}</div></div></a></li>`;
    }).join('');
    const viewAll = `<li class="predictive-search__item js-submit" role="option"><button class="predictive-search__view-all link w-full text-start focus-inset has-ltr-icon js-search-link">${esc(tpl(T.view_all, { count: res.found }))} <svg class="icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 12h14M13 6l6 6-6 6"/></svg></button></li>`;
    return `<div class="predictive-search absolute bg-theme-bg text-theme-text text-start"><div class="predictive-search__scroller overflow-auto"><div class="predictive-search__results flex flex-col"><div class="predictive-search__tabpanel focus-inset" id="${esc(inputId)}-products" role="tabpanel" tabindex="0" aria-label="${esc(T.products || 'Produkte')}"><ul class="predictive-search__list" role="list">${items}${viewAll}</ul></div></div></div></div>`;
  }

  customElements.whenDefined('predictive-search').then(() => {
    const PS = customElements.get('predictive-search');
    const nativeGetResults = PS.prototype.getResults;
    PS.prototype.getResults = async function tsGetResults(term) {
      preconnect();
      this.setLiveRegionLoadingState();
      const key = `ts:${term.toLowerCase()}`;
      if (this.cachedResults[key]) { this.renderResults(this.cachedResults[key]); return; }
      try {
        if (this.tsAbort) this.tsAbort.abort();
        this.tsAbort = new AbortController();
        const res = await search({ q: term, per_page: cfg.limit || 6, include_fields: FIELDS, sort_by: SORTS.relevance }, this.tsAbort.signal);
        const html = autocompleteHtml(res, this.input.id, term);
        this.cachedResults[key] = html;
        this.renderResults(html);
      } catch (e) {
        if (e.name === 'AbortError') return;
        console.warn('[ts-search] Autocomplete-Fallback auf native Suche:', e.message);
        return nativeGetResults.call(this, term);
      }
    };
    document.querySelectorAll('.js-search-input').forEach((el) => el.addEventListener('focus', preconnect, { once: true }));
  });

  /* ---------- 2. Suchseite ---------- */
  const root = document.querySelector('[data-ts-search]');
  if (!root) return;
  const nativeEl = document.querySelector('[data-ts-native]');
  const url = new URL(location.href);
  const q = (root.dataset.q || url.searchParams.get('q') || '').trim();
  if (!q) { showNative(); return; }
  root.hidden = false;
  root.classList.add('is-loading');
  const perPage = Number(root.dataset.perPage) || 24;
  const gridClasses = root.dataset.gridClasses || 'grid-cols-2 sm:grid-cols-3 xl:grid-cols-4';
  const list = (v) => (v ? v.split(',').filter(Boolean) : []);
  // Listen-Facetten (Reihenfolge = Anzeige). Zustand hat genau zwei Werte (Werner 03.09.).
  const COND_ORDER = ['neu', 'cpo'];
  const FACETS = [
    { key: 'condition', label: () => T.condition, fixed: COND_ORDER, labelFn: (v) => T[`cond_${v}`] || v, open: true },
    { key: 'vendor', label: () => T.brand, open: true },
    { key: 'werk', label: () => T.f_werk },
    { key: 'zifferblatt', label: () => T.f_zifferblatt },
    { key: 'gehaeuse', label: () => T.f_gehaeuse },
    { key: 'band', label: () => T.f_band },
    { key: 'wasserdicht', label: () => T.f_wasserdicht, numeric: true },
    { key: 'funktionen', label: () => T.f_funktionen },
    { key: 'merkmale', label: () => T.f_merkmale },
  ];
  const state = {
    page: 1,
    sort: SORTS[url.searchParams.get('sort')] ? url.searchParams.get('sort') : 'relevance',
    inStock: url.searchParams.get('f_stock') === '1',
    priceMin: url.searchParams.get('f_min') || '',
    priceMax: url.searchParams.get('f_max') || '',
    dMin: url.searchParams.get('f_dmin') || '',
    dMax: url.searchParams.get('f_dmax') || '',
  };
  for (const f of FACETS) state[f.key] = list(url.searchParams.get(`f_${f.key}`));
  let total = 0, shown = 0, priceStats = null, dStats = null, abort = null;

  // Facettenwerte merken, damit bei aktivem Filter die anderen Werte wählbar bleiben (Count 0)
  const knownFacets = Object.fromEntries(FACETS.map((f) => [f.key, new Map()]));
  const quote = (v) => `\`${String(v).replace(/`/g, '')}\``;
  const rangeClause = (field, minS, maxS) => {
    const min = parseFloat(minS), max = parseFloat(maxS);
    if (!isNaN(min) && !isNaN(max)) return `${field}:[${min}..${max}]`;
    if (!isNaN(min)) return `${field}:>=${min}`;
    if (!isNaN(max)) return `${field}:<=${max}`;
    return '';
  };
  const filterBy = () => {
    const f = [];
    for (const fd of FACETS) {
      const vals = fd.fixed ? state[fd.key].filter((v) => fd.fixed.includes(v)) : state[fd.key];
      if (vals.length) f.push(`${fd.key}:=[${vals.map(quote).join(',')}]`);
    }
    if (state.inStock) f.push('in_stock:=true');
    const pr = rangeClause('price', state.priceMin, state.priceMax);
    if (pr) f.push(pr, 'preis_auf_anfrage:=false');
    const dr = rangeClause('durchmesser_mm', state.dMin, state.dMax);
    if (dr) f.push(dr);
    return f.join(' && ');
  };
  const syncUrl = () => {
    const u = new URL(location.href);
    const set = (k, v) => (v ? u.searchParams.set(k, v) : u.searchParams.delete(k));
    set('sort', state.sort === 'relevance' ? '' : state.sort);
    for (const fd of FACETS) set(`f_${fd.key}`, state[fd.key].join(','));
    set('f_stock', state.inStock ? '1' : ''); set('f_min', state.priceMin); set('f_max', state.priceMax);
    set('f_dmin', state.dMin); set('f_dmax', state.dMax);
    u.searchParams.delete('page');
    history.replaceState(null, '', u.toString());
  };

  const body = root.querySelector('[data-ts-body]') || root;
  body.innerHTML = `
    <div class="ts-search__layout">
      <details class="ts-facets" ${window.matchMedia('(min-width: 1024px)').matches ? 'open' : ''}>
        <summary class="ts-facets__summary">${esc(T.filters)}<span class="ts-facets__active"></span></summary>
        <button type="button" class="ts-facets__reset link" hidden>${esc(T.reset)}</button>
        <div class="ts-facets__groups"></div>
      </details>
      <div class="ts-results">
        <ul class="ts-results__grid grid ${esc(gridClasses)} gap-x-theme gap-y-8 md:gap-y-12" role="list"></ul>
        <p class="ts-results__empty" hidden></p>
        <div class="ts-results__more"><button type="button" class="btn btn--secondary ts-results__load" hidden>${esc(T.load_more)}</button></div>
      </div>
    </div>`;
  const $ = (s) => root.querySelector(s);
  const countEl = $('.ts-search__count'), grid = $('.ts-results__grid'), emptyEl = $('.ts-results__empty'), loadBtn = $('.ts-results__load'), groupsEl = $('.ts-facets__groups'), resetBtn = $('.ts-facets__reset'), activeEl = $('.ts-facets__active');

  // Sortier-Dropdown (custom-select wie auf Collection-Seiten): Zustand aus URL spiegeln
  const sortEl = root.querySelector('#ts-sort-by');
  function syncSortUi() {
    if (!sortEl) return;
    const options = sortEl.querySelectorAll('.custom-select__option');
    options.forEach((o) => { const on = o.dataset.value === state.sort; o.setAttribute('aria-selected', on ? 'true' : 'false'); if (on) { const btnText = sortEl.querySelector('.custom-select__btn span'); if (btnText) btnText.textContent = o.textContent.trim(); sortEl.querySelector('.custom-select__listbox')?.setAttribute('aria-activedescendant', o.id); } });
  }
  if (sortEl) {
    syncSortUi();
    sortEl.addEventListener('change', (e) => {
      const v = e.detail && e.detail.selectedValue;
      if (!v || !SORTS[v] || v === state.sort) return;
      state.sort = v; state.page = 1; syncUrl(); run();
    });
  }

  function cardHtml(d, idx) {
    const badges = [];
    if (!d.available) { if (!d.preis_auf_anfrage) badges.push(`<span class="watch-card-badge watch-card-badge--sold">${esc(T.badge_sold)}</span>`); } // Werner 30.10.2026: bei „Preis auf Anfrage“ kein „Verkauft“
    else if (isNew(d)) badges.push(`<span class="watch-card-badge watch-card-badge--new">${esc(T.badge_new)}</span>`);
    else if (onSale(d)) badges.push(`<span class="watch-card-badge watch-card-badge--sale">${esc(T.badge_sale)}</span>`);
    if (isCpo(d)) badges.push(`<span class="watch-card-badge watch-card-badge--cpo">${esc(T.badge_cpo)}</span>`);
    const alt = d.referenz ? `${d.title} Ref. ${d.referenz}` : d.title;
    const media = d.image
      ? `<div class="watch-card-media__inner" style="padding-top:100%"><img class="watch-card-media__img" src="${esc(img(d.image, 460))}" srcset="${esc(img(d.image, 320))} 320w, ${esc(img(d.image, 460))} 460w, ${esc(img(d.image, 600))} 600w, ${esc(img(d.image, 800))} 800w" sizes="(max-width: 600px) 50vw, (max-width: 1023px) 33vw, 25vw" alt="${esc(alt)}" loading="${idx < 6 ? 'eager' : 'lazy'}" decoding="async"></div>`
      : `<div class="watch-card-media__inner watch-card-media__inner--empty" style="padding-top:100%"><svg class="watch-card-media__placeholder" viewBox="0 0 100 100" fill="none" stroke="#ccc" stroke-width="2" aria-hidden="true"><circle cx="50" cy="50" r="38"/></svg></div>`;
    return `<li class="js-pagination-result"><product-card class="card card--product card--watch h-full relative flex"><a class="watch-card-link" href="${esc(productUrl(d))}" aria-label="${esc(d.title)}"><div class="watch-card-media">${media}${badges.join('')}</div><div class="watch-card-info">${d.vendor ? `<div class="watch-card-vendor">${esc(String(d.vendor).toUpperCase())}</div>` : ''}<div class="watch-card-title">${esc(d.title)}</div>${d.referenz ? `<div class="watch-card-meta">${esc(T.ref)} ${esc(d.referenz)}</div>` : ''}<div class="watch-card-price-row">${priceHtml(d, 'watch-card-price')}</div>${d.in_stock && d.available ? `<div class="watch-card-availability watch-card-availability--is-available"><span class="watch-card-availability__dot" aria-hidden="true"></span><span>${esc(T.in_stock)}</span></div>` : ''}</div></a></product-card></li>`;
  }

  const numVal = (v) => { const m = String(v).replace(',', '.').match(/-?\d+(\.\d+)?/); return m ? parseFloat(m[0]) : Number.POSITIVE_INFINITY; };
  const rangeHtml = (title, keyMin, keyMax, stats, unit, openByDefault = true) => `<details class="ts-facet"${openByDefault || state[keyMin] || state[keyMax] ? ' open' : ''}><summary class="ts-facet__title">${esc(title)}</summary><div class="ts-facet__price"><label class="ts-facet__price-field"><span class="ts-facet__price-label">${esc(T.price_min)}${unit ? ' ' + esc(unit) : ''}</span><input type="number" inputmode="decimal" min="0" placeholder="${stats ? stats.min : ''}" data-range="${keyMin}" value="${esc(state[keyMin])}"></label><label class="ts-facet__price-field"><span class="ts-facet__price-label">${esc(T.price_max)}${unit ? ' ' + esc(unit) : ''}</span><input type="number" inputmode="decimal" min="0" placeholder="${stats ? stats.max : ''}" data-range="${keyMax}" value="${esc(state[keyMax])}"></label><button type="button" class="btn btn--secondary ts-facet__apply">${esc(T.apply)}</button></div></details>`;

  function facetsHtml(fc) {
    const by = Object.fromEntries((fc || []).map((f) => [f.field_name, f]));
    const checks = (fd, values) => {
      if (!values.length) return '';
      const open = fd.open || state[fd.key].length > 0;   // Nebengruppen zu; aktive Filter öffnen ihre Gruppe
      return `<details class="ts-facet"${open ? ' open' : ''}><summary class="ts-facet__title">${esc(fd.label())}</summary><ul class="ts-facet__list" role="list">${values.map((v) => `<li><label class="ts-facet__option"><input type="checkbox" data-facet="${fd.key}" value="${esc(v.value)}"${state[fd.key].includes(v.value) ? ' checked' : ''}> <span>${esc(fd.labelFn ? fd.labelFn(v.value) : v.value)}</span> <span class="ts-facet__count">${v.count}</span></label></li>`).join('')}</ul></details>`;
    };
    const merge = (fd, counts) => {
      const cur = new Map((counts || []).map((c) => [c.value, c.count]));
      const selected = new Set(state[fd.key]);
      for (const v of cur.keys()) knownFacets[fd.key].set(v, true);
      let values = selected.size ? [...knownFacets[fd.key].keys()] : [...cur.keys()];
      if (fd.fixed) values = fd.fixed.slice();   // feste Werte immer zeigen (auch mit 0)
      let rows = values.map((v) => ({ value: v, count: cur.get(v) || 0 })).filter((c) => fd.fixed || c.count > 0 || selected.has(c.value) || selected.size);
      if (fd.fixed) rows.sort((a, b) => fd.fixed.indexOf(a.value) - fd.fixed.indexOf(b.value));
      else if (fd.numeric) rows.sort((a, b) => numVal(a.value) - numVal(b.value));
      else rows.sort((a, b) => b.count - a.count || a.value.localeCompare(b.value, cfg.locale || 'de'));
      return rows;
    };
    const stockCount = ((by.in_stock?.counts) || []).find((c) => c.value === 'true')?.count || 0;
    const stock = `<details class="ts-facet" open><summary class="ts-facet__title">${esc(T.availability)}</summary><ul class="ts-facet__list" role="list"><li><label class="ts-facet__option"><input type="checkbox" data-facet="inStock"${state.inStock ? ' checked' : ''}> <span>${esc(T.in_stock_only)}</span> <span class="ts-facet__count">${stockCount}</span></label></li></ul></details>`;
    const ps = by.price?.stats; if (ps && priceStats === null) priceStats = { min: Math.floor(ps.min), max: Math.ceil(ps.max) };
    const ds = by.durchmesser_mm?.stats; if (ds && dStats === null) dStats = { min: Math.floor(ds.min), max: Math.ceil(ds.max) };
    const groups = FACETS.map((fd) => ({ fd, html: checks(fd, merge(fd, by[fd.key]?.counts)) }));
    const g = (key) => groups.find((x) => x.fd.key === key)?.html || '';
    // Reihenfolge wie die nativen Collection-Filter: Verfügbarkeit, Zustand, Preis, Marke, Durchmesser, Werk, Zifferblatt, Gehäuse, Band, Wasserdicht, Funktionen, Weitere Merkmale
    groupsEl.innerHTML = stock + g('condition') + rangeHtml(T.price, 'priceMin', 'priceMax', priceStats, '') + g('vendor')
      + rangeHtml(T.f_durchmesser, 'dMin', 'dMax', dStats, T.unit_mm, false) + g('werk') + g('zifferblatt') + g('gehaeuse') + g('band') + g('wasserdicht') + g('funktionen') + g('merkmale');
    const active = FACETS.reduce((n, fd) => n + state[fd.key].length, 0) + (state.inStock ? 1 : 0) + (state.priceMin || state.priceMax ? 1 : 0) + (state.dMin || state.dMax ? 1 : 0);
    activeEl.textContent = active ? ` (${active})` : '';
    resetBtn.hidden = !active;
  }

  async function run(append = false) {
    if (abort) abort.abort();
    abort = new AbortController();
    root.classList.add('is-loading');
    loadBtn.disabled = true;
    try {
      const res = await search({
        q, page: state.page, per_page: perPage, sort_by: SORTS[state.sort], include_fields: FIELDS,
        facet_by: 'in_stock,price,durchmesser_mm,' + FACETS.map((f) => f.key).join(','), max_facet_values: 80, filter_by: filterBy() || undefined,
      }, abort.signal);
      total = res.found; shown = (res.page - 1) * perPage + (res.hits || []).length;
      root.hidden = false;
      if (nativeEl) nativeEl.hidden = true;
      if (!append) grid.innerHTML = '';
      grid.insertAdjacentHTML('beforeend', (res.hits || []).map((h, i) => cardHtml(h.document, (res.page - 1) * perPage + i)).join(''));
      emptyEl.hidden = total > 0;
      if (!total) emptyEl.textContent = tpl(T.no_results, { terms: q });
      countEl.textContent = total === 1 ? tpl(T.results_one, { terms: q }) : tpl(T.results_count, { count: total.toLocaleString(cfg.locale || 'de'), terms: q });
      loadBtn.hidden = shown >= total;
      loadBtn.textContent = `${T.load_more} (${shown}/${total})`;
      if (!append) facetsHtml(res.facet_counts);
      // Seitentitel zeigt die native Trefferzahl -> an Typesense angleichen (erste Zahl im Titel)
      if (!append && /\d/.test(document.title)) document.title = document.title.replace(/\d[\d.,]*/, total.toLocaleString(cfg.locale || 'de'));
    } catch (e) {
      if (e.name === 'AbortError') return;
      console.warn('[ts-search] Fehler:', e.message);
      if (append) { loadBtn.hidden = false; return; }   // Nachladen fehlgeschlagen: Liste behalten
      root.hidden = true;
      showNative();
    } finally { root.classList.remove('is-loading'); loadBtn.disabled = false; }
  }

  root.addEventListener('change', (e) => {
    const el = e.target;
    if (!(el instanceof HTMLInputElement)) return;
    if (el.dataset.facet && FACETS.some((f) => f.key === el.dataset.facet)) {
      const key = el.dataset.facet; const set = new Set(state[key]);
      el.checked ? set.add(el.value) : set.delete(el.value); state[key] = [...set];
    } else if (el.matches('[data-facet="inStock"]')) { state.inStock = el.checked; }
    else return;
    state.page = 1; syncUrl(); run();
  });
  root.addEventListener('click', (e) => {
    if (e.target.closest('.ts-facet__apply')) {
      root.querySelectorAll('[data-range]').forEach((inp) => { state[inp.dataset.range] = inp.value.trim(); });
      state.page = 1; syncUrl(); run();
    } else if (e.target.closest('.ts-results__load')) { state.page += 1; run(true); }
    else if (e.target.closest('.ts-facets__reset')) {
      Object.assign(state, { page: 1, inStock: false, priceMin: '', priceMax: '', dMin: '', dMax: '' });
      for (const fd of FACETS) state[fd.key] = [];
      syncUrl(); run();
    }
  });
  root.addEventListener('keydown', (e) => { if (e.key === 'Enter' && e.target.matches('[data-range]')) { e.preventDefault(); e.target.closest('.ts-facet').querySelector('.ts-facet__apply').click(); } });
  preconnect();
  run();
})();
